import type { PublicationCode } from '@/scopes/common/domain/models'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useEnv } from '@/scopes/common/infrastructure/env'
import { QuickSearchBarHeight } from '@/scopes/search/components/QuickSearchBar'
import { getImagePositioning, getSeason } from '@/scopes/search/quick-search/domain/policies/images'
import { getPlpHeroImageUrl } from '@/scopes/search/search/domain/policies/publicationCodes'
import { mqMin, tok, tokPx, zIndex } from '@sunwebgroup/ui-components'
import { PropsWithChildren, useMemo } from 'react'
import { styled } from 'styled-components'

const Wrapper = styled.div`
  width: 100%;
  user-select: none;
  background: ${tok('semantics_views_tinted_background_color')};
  display: flex;
  position: relative;
`

const Content = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  user-select: none;
  width: 100%;
  z-index: ${zIndex.level3};
  height: calc(${QuickSearchBarHeight} + ${tokPx('primitives_space_16')});

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: calc(${QuickSearchBarHeight} + ${tokPx('primitives_space_32')});
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    height: calc(${QuickSearchBarHeight} + ${tokPx('primitives_space_48')});
  }
`

const BackgroundImage = styled.img<{ $positioning?: string }>`
  object-fit: cover;
  width: 100%;
  height: ${tokPx('primitives_area_size_160')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: ${tokPx('primitives_area_size_200')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    height: ${tokPx('primitives_area_size_240')};
  }

  @media (min-width: 460px) {
    object-position: ${({ $positioning }) => $positioning};
  }
`

function Hero({ children }: PropsWithChildren) {
  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL')
  const publicationCode = useConfiguration('publicationCode')
  const positioning = getImagePositioning(publicationCode)

  const imageUrl = useMemo(() => getPlpHeroImageUrl(publicationCode as PublicationCode, mediaServerBaseUrl), [
    publicationCode,
    mediaServerBaseUrl,
  ])
  const currentSeason = useMemo(() => getSeason(publicationCode), [publicationCode])

  const buildImageProps = () => {
    // If there's no image for this site/season, return a safe, invisible fallback
    if (!imageUrl) {
      return {
        src: '',
        srcSet: '',
        sizes: '',
        alt: '',
        fetchpriority: 'high' as const,
        $positioning: positioning,
      }
    }

    const src = buildImgSrc(imageUrl, 375)
    const srcSet = buildImgSrcSet(imageUrl, [375, 600, 720, 1440, 2000, 2400])

    return {
      src,
      srcSet,
      sizes: '100vw',
      alt: '',
      fetchpriority: 'high' as const,
      $positioning: positioning,
    }
  }

  const imageProps = useMemo(() => buildImageProps(), [imageUrl, currentSeason])

  return (
    <Wrapper>
      <BackgroundImage {...imageProps} />
      <Content>{children}</Content>
    </Wrapper>
  )
}

export { Hero }
